import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useButtonScroll } from '@/shared/hooks/dom/scroll/useButtonScroll';
import { useDragScroll } from '@/shared/hooks/dom/scroll/useDragScroll';
import { useScrollVisibility } from '@/shared/hooks/dom/scroll/useScrollVisibility';
import { useSliderScroll } from '@/shared/hooks/dom/scroll/useSliderScroll';
import { classNames } from '@/shared/lib/classnames/classnames';
import type { ButtonVariants } from '@/shared/ui/button';
import { SliderButtons } from './SliderButtons';
interface Props {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  itemTag?: React.ElementType;
  activeOnView?: boolean;
  scrollByItem?: boolean;
  btnBgVariant?: ButtonVariants;
  onViewIndexChange?: (idx: number) => void;
  testId?: string;
}
const Slider = forwardRef<HTMLDivElement, Props>(({
  children,
  itemTag = 'div',
  containerClassName = '',
  className = '',
  activeOnView = false,
  scrollByItem = false,
  btnBgVariant = {
    variant: 'default'
  },
  onViewIndexChange,
  testId
}, forwardedRef) => {
  const t = useTranslations();
  const innerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => innerRef.current!);
  const {
    showLeftControl,
    showRightControl
  } = useScrollVisibility(innerRef);
  const {
    dragHandlers
  } = useDragScroll(innerRef, {
    dragSpeedMultiplier: 2,
    dragThreshold: 10
  });
  const {
    scrollLeft,
    scrollRight
  } = useButtonScroll(innerRef, {
    gap: 4,
    tagName: itemTag
  });
  const {
    scrollByItemWidth,
    isScrollByItem,
    viewIndex
  } = useSliderScroll(innerRef, {
    activeOnView,
    scrollByItem
  });
  const handleScrollRight = (direction: 'left' | 'right') => {
    if (isScrollByItem) {
      scrollByItemWidth(direction);
    } else {
      if (direction === 'left') {
        scrollLeft();
      } else {
        scrollRight();
      }
    }
  };
  useEffect(() => {
    if (typeof onViewIndexChange === 'function') {
      onViewIndexChange(viewIndex);
    }
  }, [viewIndex, onViewIndexChange]);
  return <article data-testid={testId} className={classNames('relative w-full', {}, [containerClassName])}>
        {showLeftControl && <SliderButtons direction="left" bgVariant={btnBgVariant} onClick={() => handleScrollRight('left')} ariaLabel={t('shared.aria.prev')} />}
        <div ref={innerRef} className={classNames(`flex items-center gap-2 overflow-x-auto scroll-width-none`, {}, [className])} {...dragHandlers}>
          {children}
        </div>
        {showRightControl && <SliderButtons direction="right" bgVariant={btnBgVariant} onClick={() => handleScrollRight('right')} ariaLabel={t('shared.aria.next')} />}
      </article>;
});
Slider.displayName = 'Slider';
export { Slider };